import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../../components/common/ThemedText';
import { getMyPayments, ClubPayment } from '../../lib/api/payments';
import { showErrorToast } from '../../utils/toast';

const MyPaymentsScreen: React.FC = () => {
  const { theme } = useTheme();
  const navigation = useNavigation();

  const [payments, setPayments] = useState<ClubPayment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadPayments = useCallback(async (refreshing = false) => {
    try {
      if (refreshing) setIsRefreshing(true);
      else setIsLoading(true);

      const data = await getMyPayments();
      setPayments(data);
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to load payments');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const getStatusColor = (status: ClubPayment['status']) => {
    switch (status) {
      case 'paid': return '#10B981';
      case 'pending': return '#F59E0B';
      case 'failed': return theme.error;
      case 'refunded': return theme.textSecondary;
      default: return theme.textSecondary;
    }
  };

  const getStatusIcon = (status: ClubPayment['status']): keyof typeof Ionicons['glyphMap'] => {
    switch (status) {
      case 'paid': return 'checkmark-circle';
      case 'pending': return 'time';
      case 'failed': return 'close-circle';
      case 'refunded': return 'refresh-circle';
      default: return 'help-circle';
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const renderPayment = ({ item }: { item: ClubPayment }) => (
    <View style={[styles.paymentCard, { backgroundColor: theme.card, borderColor: theme.border }]}>
      <View style={[styles.cardIconCircle, { backgroundColor: theme.primary + '20' }]}>
        <Ionicons name="people" size={24} color={theme.primary} />
      </View>

      <View style={styles.cardBody}>
        <ThemedText style={[styles.clubName, { color: theme.text }]} numberOfLines={1}>
          {item.clubName}
        </ThemedText>
        <ThemedText style={[styles.dateText, { color: theme.textSecondary }]}>
          {item.paidAt ? formatDate(item.paidAt) : formatDate(item.createdAt)}
        </ThemedText>
        {item.razorpayPaymentId && (
          <ThemedText style={[styles.txnId, { color: theme.textTertiary }]} numberOfLines={1}>
            Txn: {item.razorpayPaymentId}
          </ThemedText>
        )}
      </View>

      <View style={styles.cardRight}>
        <ThemedText style={[styles.amountText, { color: theme.text }]}>
          ₹{item.amount.toLocaleString('en-IN')}
        </ThemedText>
        <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) + '20' }]}>
          <Ionicons name={getStatusIcon(item.status)} size={12} color={getStatusColor(item.status)} />
          <ThemedText style={[styles.statusText, { color: getStatusColor(item.status) }]}>
            {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
          </ThemedText>
        </View>
      </View>
    </View>
  );

  const renderEmpty = () => (
    <View style={styles.emptyContainer}>
      <Ionicons name="receipt-outline" size={56} color={theme.textTertiary} />
      <ThemedText style={[styles.emptyTitle, { color: theme.text }]}>No payments yet</ThemedText>
      <ThemedText style={[styles.emptySubtext, { color: theme.textSecondary }]}>
        Your club membership payments will appear here.
      </ThemedText>
    </View>
  );

  const totalPaid = payments
    .filter((p) => p.status === 'paid')
    .reduce((sum, p) => sum + p.amount, 0);

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: theme.backgroundSecondary, borderBottomColor: theme.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} activeOpacity={0.7}>
          <Ionicons name="arrow-back" size={24} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>My Payments</ThemedText>
        <View style={{ width: 24 }} />
      </View>

      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.primary} />
        </View>
      ) : (
        <FlatList
          data={payments}
          keyExtractor={(item) => item.id}
          renderItem={renderPayment}
          ListEmptyComponent={renderEmpty}
          contentContainerStyle={[styles.listContent, payments.length === 0 && styles.listContentEmpty]}
          refreshControl={
            <RefreshControl
              refreshing={isRefreshing}
              onRefresh={() => loadPayments(true)}
              tintColor={theme.primary}
            />
          }
          ListHeaderComponent={
            payments.length > 0 ? (
              <View style={[styles.summaryBanner, { backgroundColor: theme.primary + '18', borderColor: theme.primary + '40' }]}>
                <Ionicons name="card" size={20} color={theme.primary} />
                <ThemedText style={[styles.summaryText, { color: theme.text }]}>
                  Total paid: <ThemedText style={[styles.summaryAmount, { color: theme.primary }]}>
                    ₹{totalPaid.toLocaleString('en-IN')}
                  </ThemedText>
                </ThemedText>
              </View>
            ) : null
          }
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  listContent: {
    padding: 16,
    gap: 12,
  },
  listContentEmpty: {
    flex: 1,
  },
  summaryBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderRadius: 10,
    borderWidth: 1,
    padding: 12,
    marginBottom: 4,
  },
  summaryText: {
    fontSize: 14,
  },
  summaryAmount: {
    fontWeight: '700',
  },
  paymentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    borderWidth: 1,
    padding: 14,
    gap: 12,
  },
  cardIconCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardBody: {
    flex: 1,
    gap: 3,
  },
  clubName: {
    fontSize: 15,
    fontWeight: '600',
  },
  dateText: {
    fontSize: 12,
  },
  txnId: {
    fontSize: 11,
  },
  cardRight: {
    alignItems: 'flex-end',
    gap: 6,
  },
  amountText: {
    fontSize: 15,
    fontWeight: '700',
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 8,
  },
  statusText: {
    fontSize: 11,
    fontWeight: '600',
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
    gap: 12,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  emptySubtext: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
  },
});

export default MyPaymentsScreen;
